import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Sun, Mail, Globe, Sparkles, AlertCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 4 — gebrandeter Agentur-Portal-Startscreen.
 * Route: /portal/:agency_slug
 *
 * - lädt Agentur-Branding über GET /api/public/agencies/{slug}
 * - optional: wenn aufgerufen über eigene Custom-Portal-Domain → Lookup per host
 * - CTA leitet zu /login?agency={slug}&return_url=/dashboard
 *
 * KEIN Login-Logik, KEIN Mandantendaten — nur gebrandeter Einstieg.
 */
export default function AgencyPortal() {
  const { agency_slug } = useParams();
  const nav = useNavigate();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const host = (window.location.hostname || "").toLowerCase();
        // Default-Hosts (Apex + dev/preview) via REACT_APP_DEFAULT_HOST_KEYWORDS überschreibbar
        const defaultKeywords = (process.env.REACT_APP_DEFAULT_HOST_KEYWORDS || "revenioo,offerflow,localhost,127.0.0.1")
          .split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
        const isStandardHost = !host || defaultKeywords.some((kw) => host.includes(kw));
        // 1) Wenn Custom-Domain → Lookup by-domain
        if (!isStandardHost) {
          try {
            const r = await axios.get(`${BACKEND}/api/public/agencies/by-domain`, { params: { host } });
            if (!cancelled) setAgency(r.data);
            return;
          } catch (e) {
            // Fallthrough zur Slug-Suche
          }
        }
        // 2) Standardweg: per Slug
        if (!agency_slug) {
          if (!cancelled) setError("Kein Portal-Slug angegeben.");
          return;
        }
        const r = await axios.get(`${BACKEND}/api/public/agencies/${encodeURIComponent(agency_slug)}`);
        if (!cancelled) setAgency(r.data);
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404) setError("Portal nicht gefunden.");
          else setError("Portal konnte nicht geladen werden.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [agency_slug]);

  // Phase 4.1: View-Tracking (1× pro Session)
  useEffect(() => {
    if (!agency?.agency_slug) return;
    const key = `portal_view_${agency.agency_slug}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    axios.post(`${BACKEND}/api/public/agencies/${encodeURIComponent(agency.agency_slug)}/view`).catch(() => {});
  }, [agency?.agency_slug]);

  const goLogin = () => {
    const slug = agency?.agency_slug || agency_slug || "";
    const qs = new URLSearchParams({ agency: slug, return_url: "/dashboard" });
    nav(`/login?${qs.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500 text-sm">Portal wird geladen …</p>
      </div>
    );
  }

  if (error || !agency) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="p-8 max-w-md w-full text-center space-y-4 border-slate-200" data-testid="portal-error-card">
          <AlertCircle className="w-10 h-10 text-slate-400 mx-auto" />
          <h1 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Portal nicht gefunden
          </h1>
          <p className="text-sm text-slate-500">{error || "Die angeforderte Agentur ist aktuell nicht verfügbar."}</p>
          <Button onClick={() => nav("/")} variant="outline" data-testid="portal-error-home-btn">
            Zur Startseite
          </Button>
        </Card>
      </div>
    );
  }

  const primary = agency.primary_color || "#f97316";
  const accent = agency.accent_color || "#0f172a";

  return (
    <div
      data-testid="agency-portal-page"
      className="min-h-screen flex flex-col"
      style={{ background: `radial-gradient(1200px 600px at 10% -10%, ${primary}1F, transparent 50%), radial-gradient(800px 400px at 100% 110%, ${accent}1A, transparent 50%), #0b1220` }}
    >
      {/* Subtle Grid Overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <header className="relative z-10 px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="portal-brand">
          {agency.logo_url ? (
            <img
              src={agency.logo_url}
              alt={agency.name}
              className="h-10 max-w-[160px] object-contain bg-white/10 rounded-lg p-1.5 backdrop-blur-sm border border-white/15"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div
              className="h-10 w-10 rounded-lg flex items-center justify-center"
              style={{ background: primary }}
            >
              <Sun className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
          )}
          <div>
            <p className="text-white font-extrabold leading-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
              {agency.name}
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/55">Solar Sales Portal</p>
          </div>
        </div>
        {agency.website && (
          <a
            href={agency.website}
            target="_blank"
            rel="noreferrer"
            className="hidden md:flex items-center gap-1.5 text-xs text-white/70 hover:text-white"
            data-testid="portal-website-link"
          >
            <Globe className="w-3.5 h-3.5" /> {agency.website.replace(/^https?:\/\//, "")}
          </a>
        )}
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 md:px-10 py-10">
        <div className="w-full max-w-2xl">
          <Card className="border-white/10 bg-white/95 backdrop-blur-md p-8 md:p-12 shadow-2xl space-y-6" data-testid="portal-login-card">
            <span
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-md"
              style={{ background: `${primary}18`, color: primary }}
            >
              <Sparkles className="w-3 h-3" /> Solar Sales Portal
            </span>
            <h1
              className="text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Willkommen im Solar Sales Portal von{" "}
              <span style={{ color: primary }}>{agency.name}</span>.
            </h1>
            <p className="text-slate-600 text-base md:text-lg">
              Verwalten Sie Ihre PV-Leads, Angebote, Follow-ups und Hot Leads zentral an einem Ort.
            </p>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                data-testid="portal-login-btn"
                onClick={goLogin}
                className="h-12 px-6 text-base font-semibold text-white"
                style={{ background: accent }}
              >
                Einloggen <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              {agency.support_email && (
                <a
                  href={`mailto:${agency.support_email}`}
                  className="inline-flex items-center gap-2 h-12 px-4 rounded-md border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
                  data-testid="portal-support-link"
                >
                  <Mail className="w-4 h-4" /> {agency.support_email}
                </a>
              )}
            </div>
          </Card>

          <p className="text-center text-xs text-white/55 mt-6" data-testid="portal-powered-by">
            Powered by <span className="font-semibold text-white/80">revenioo.ai</span>
          </p>

          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-white/45" data-testid="portal-legal-links">
            {agency.imprint_url ? (
              <a href={agency.imprint_url} target="_blank" rel="noreferrer" className="hover:text-white/80" data-testid="portal-legal-agency-imprint">Impressum {agency.name}</a>
            ) : (
              <Link to="/impressum" className="hover:text-white/80" data-testid="portal-legal-imprint">Impressum</Link>
            )}
            <span>·</span>
            {agency.privacy_url ? (
              <a href={agency.privacy_url} target="_blank" rel="noreferrer" className="hover:text-white/80" data-testid="portal-legal-agency-privacy">Datenschutz {agency.name}</a>
            ) : (
              <Link to="/datenschutz" className="hover:text-white/80" data-testid="portal-legal-privacy">Datenschutz</Link>
            )}
            <span>·</span>
            <Link to="/cookies" className="hover:text-white/80" data-testid="portal-legal-cookies">Cookies</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
